import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import type { SuggestionsLoadState } from "../../contexts/sidePanel/sidePanelTypes";
import { useWeekViewContext } from "../../contexts/WeekView/WeekViewContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../hooks/useDepartmentNurseTypes";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRosterLoadState } from "../../hooks/useRosterLookup";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import { useShiftTimes } from "../../hooks/useShiftTimes";
import { getStaffingIssues, type StaffingIssue, type StaffingParams, withoutSwapSuggestions } from "../../utils/autoStaffing";
import { getWeekStart } from "../../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY, IssuePriority, type ScheduleData } from "../../utils/models";
import { dayIndexOfDate, getFirstLoadedDate, weekIndexOfDate } from "../../utils/panelWeeks";

/**
 * The in-browser engine's suggestions for the Day view's day, found in the weeks the panels read around it (the cache
 * with `staffing_schedule_cache` on, so any day of the schedule has them). The suggestions' positions point into
 * `schedule`, which the cards are given with them.
 */
export const useDayScheduleStaffingIssues = (date: DateTime) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isSnf } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { calculateShiftTimes } = useShiftTimes();
	const { scheduleNurses } = useWeekViewContext();
	// `scheduleNurses` is the staff list: until it's in, the engine has no one to suggest.
	const staffListLoad = useRosterLoadState(chosenDepartment.department_id);
	const { staffRequests } = useRequestsContext();
	// The engine reads the weeks either side of the day's too (a shift the day before or after, days in a row).
	const { weeks: panelWeeks, isLoading: areWeeksLoading, isScheduleError: areWeeksFailed } = useScheduleWeeks(date);
	const suggestionsLoad = useMemo<SuggestionsLoadState>(
		() => ({ isLoading: staffListLoad.isLoading || areWeeksLoading, isError: staffListLoad.isError, isWeekError: areWeeksFailed }),
		[staffListLoad.isLoading, staffListLoad.isError, areWeeksLoading, areWeeksFailed],
	);
	// A week past either end of the schedule has no shift rows, and the engine reads every week it is given by row:
	// leaving those weeks out makes them "before the first week" or "after the last", which it already handles.
	const weeks = useMemo(() => panelWeeks.filter((week) => week.weeklyShifts.length > 0), [panelWeeks]);
	const isCacheEnabled = useScheduleCacheEnabled();

	const [recommendations, setRecommendations] = useState<StaffingIssue[]>([]);
	const [chosenLevel, setChosenLevel] = useState<IssuePriority>(IssuePriority.TopPriority);
	const [implementedChanges, setImplementedChanges] = useState<Record<string, boolean>>({});

	const schedule = useMemo<ScheduleData>(() => ({ weeks, nurses: scheduleNurses }), [weeks, scheduleNurses]);
	const weekIndex = weekIndexOfDate(weeks, date);
	const dayIndex = dayIndexOfDate(weeks, date);
	// The cards place a suggestion's day as days from the Sunday the weeks start on.
	// DEFAULT: with no week loaded there is no suggestion to place, so the day's own week does.
	const startingDate = useMemo<DateTime>(() => getWeekStart(getFirstLoadedDate(weeks) ?? date, DEFAULT_WEEK_START_DAY), [weeks, date]);
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const getRecommendations = useCallback((): void => {
		if (!isBlockRecommendationsV2Enabled) return;
		if (weekIndex === -1 || areWeeksLoading || areWeeksFailed) {
			setRecommendations([]);
			return;
		}

		const staffingContext: StaffingParams = {
			hospitalData: schedule,
			weekIndex,
			isSnf,
			isAutoScheduler: false,
			chosenDepartment,
			timezone: chosenDepartment.timezone,
			departmentNurseTypes,
			requests: staffRequests,
			calculateShiftTimes,
			jobManagementEnabled,
			lastNameFirst,
		};

		const issues = getStaffingIssues(staffingContext);
		const currentDayIssues = (isCacheEnabled ? withoutSwapSuggestions(issues) : issues).filter(
			(issue) => issue.suggestions[0] && issue.suggestions[0].dayNo === dayIndex && issue.suggestions[0].weekIndex === weekIndex,
		);
		setRecommendations(currentDayIssues);
	}, [
		isBlockRecommendationsV2Enabled,
		areWeeksLoading,
		areWeeksFailed,
		schedule,
		dayIndex,
		weekIndex,
		isSnf,
		chosenDepartment,
		departmentNurseTypes,
		staffRequests,
		calculateShiftTimes,
		jobManagementEnabled,
		lastNameFirst,
		isCacheEnabled,
	]);

	const handleRemoveRecommendation = useCallback((index: number): void => setRecommendations((prev) => prev.filter((_, i) => i !== index)), []);

	useEffect(() => getRecommendations(), [getRecommendations]);

	return {
		recommendations,
		getRecommendations,
		handleRemoveRecommendation,
		chosenLevel,
		setChosenLevel,
		implementedChanges,
		setImplementedChanges,
		schedule,
		weekIndex,
		startingDate,
		suggestionsLoad,
	};
};
